import { faChevronDown, faX } from "@fortawesome/free-solid-svg-icons";
import { useAtomValue, useSetAtom } from "jotai";
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { codeAtom } from "../../../atoms/location/codeAtom";
import { showBottomAndTopNavAtom } from "../../../atoms/navAtoms";
import listenWebsocketAtom from "../../../atoms/websockets/listenWebsocketAtom";
import {
  useGetFooterPlayerRef,
  useGetFooterWaveformRef,
  useHandlePause,
  usePlayPauseOnFooter,
} from "../../../hooks/audioPlayerHooks/useGetFooterPlayerRef";
import { useSetupOnFinish } from "../../../hooks/audioPlayerHooks/useSetupOnFinish";
import {
  skipSeconds,
  SkipTimes,
} from "../../../hooks/audioPlayerHooks/waveformUtils";
import useInvalidateOnboardingProgress from "../../../hooks/onboardingHooks/useInvalidateOnboardingProgress";
import useClickOutside from "../../../hooks/useClickOutside";
import { useMediaQueryBreakpoint } from "../../../hooks/useMediaQuery";
import { SCREENS } from "../../../routes/screens";
import {
  FooterFileTrackType,
  resetAbPlayStore,
  setIndex,
} from "../../../store/actions/abPlayerStore";
import { clearFileVersionDownload } from "../../../store/actions/fileVersions";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { PlaylistTrackType } from "../../../store/models/playListTrack/types";
import {
  getCurrentTrackFromPlaylist,
  getTrackInfo,
} from "../../../store/selectors/abPlayerSelectors";
import { emitAnalyticsTrackingEvent } from "../../../utils/analyticsUtils";
import { Button, ButtonVariant } from "../../core-ui/components/Button/Button";
import {
  PlaybackControlButton,
  PlaybackControlButtonVariant,
} from "../../elements/PlaybackControlButton/PlaybackControlButton";
import { PlaybackControlButtonSize } from "../../elements/PlaybackControlButton/PlaybackControlButton.styles";
import { ColorPalette } from "../../theme";
import { Playlist } from "../Playlist/Playlist";
import {
  StyledFooterPlayerActionsContainer,
  StyledFooterPlayerButtonsContainer,
  StyledFooterPlayerContainer,
  StyledFooterPlayerInfoFontawesomeIcon,
  StyledFooterPlayerPlaybackControlContainer,
  StyledFooterRowContainer,
  StyledFooterWaveformContainer,
} from "./FooterPlayer.styles";
import { FooterProjectInfo } from "./FooterProjectInfo/FooterProjectInfo";
import { FooterWaveform } from "./FooterWaveform/FooterWaveform";
import { useFetchTrackMp3 } from "../../../api/audio/useFetchTracks";
import { activeWaveformQueryKeyAtom } from "../../../atoms/audioAtom";

export const FooterPlayer = () => {
  const {
    showFooterPlayer,
    isFooterPlaying,
    playlist,
    currentTrackIndex,
    footerFileTrackType,
    isFooterReady,
  } = useAppSelector((state) => state.abPlayerStore);

  const dispatch = useAppDispatch();
  const { isDesktop } = useMediaQueryBreakpoint();
  const isFooterAndHeaderShowing = useAtomValue(showBottomAndTopNavAtom);
  const [expandFooter, setExpandFooter] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const footerPlayerContainerRef = useRef<HTMLDivElement>(null);
  const [isLooping, setIsLooping] = useState(false);
  const footerWaveformRef = useGetFooterWaveformRef();
  const currentTrack = useAppSelector(getCurrentTrackFromPlaylist());
  const code = useAtomValue(codeAtom);
  const [playCounted, setPlayCounted] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const hideFooter = location.pathname.startsWith(
    SCREENS.LOGGED_OUT_REVIEW.replace("/:project_id", ""),
  );
  const { invalidateOnboardingProgress } = useInvalidateOnboardingProgress();
  const listenWebsocket = useAtomValue(listenWebsocketAtom);
  const { handleClick: handlePlayPause } = usePlayPauseOnFooter();
  const { handlePause } = useHandlePause();
  const setActiveWaveformQueryKey = useSetAtom(activeWaveformQueryKeyAtom);
  const canChangeIndex = [
    FooterFileTrackType.SCHEDULED_PROJECT,
    FooterFileTrackType.RELEASE,
    FooterFileTrackType.PORTFOLIO_PLAYLIST_TRACK,
    FooterFileTrackType.SPOTIFY_SNIPPET,
  ].includes(footerFileTrackType);
  const audioElement = useGetFooterPlayerRef();
  const { isMobile } = useMediaQueryBreakpoint();
  const {
    artist: artistName,
    title,
    engineer: engineerName,
    artistUsername,
    engineerUsername,
    artworkSource,
    serviceType,
  } = useAppSelector(getTrackInfo(currentTrackIndex));
  const toggleFooterAndPlaylist = (show: boolean) => {
    setExpandFooter(show);
    setShowPlaylist(show);
  };

  const handleListenTracking = useCallback(
    (release_track_id: string) => {
      if (listenWebsocket && listenWebsocket.readyState === WebSocket.OPEN) {
        listenWebsocket.send(
          JSON.stringify({ release_track_id: release_track_id }),
        );
      }
    },
    [listenWebsocket],
  );

  const wsListen = useCallback(
    (releaseTrackId: string) => {
      handleListenTracking(releaseTrackId);
      setPlayCounted(true);
    },
    [handleListenTracking, setPlayCounted],
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === "Space") {
        // Toggle play/pause when space bar is pressed
        const activeElement = (event.target as HTMLElement).nodeName;
        const isTypingInField = Boolean(
          activeElement === "INPUT" || activeElement === "TEXTAREA",
        );

        if (!isFooterReady || isTypingInField || !audioElement) {
          return;
        }
        event.preventDefault();
        handlePlayPause();
      }
    },
    [isFooterReady],
  );

  const updateTimeoutId = useCallback(
    (timeoutId: NodeJS.Timeout | null) => {
      setTimeoutId((oldState) => {
        if (oldState) {
          clearTimeout(oldState);
        }
        return timeoutId;
      });
    },
    [setTimeoutId],
  );

  useEffect(() => {
    if (
      footerFileTrackType !== FooterFileTrackType.RELEASE ||
      !currentTrack?.id ||
      playCounted
    ) {
      return;
    }
    if (isFooterPlaying) {
      updateTimeoutId(
        setTimeout(() => wsListen(currentTrack.id.toString()), 5000),
      );
    }
  }, [
    footerFileTrackType,
    currentTrack?.id,
    isFooterPlaying,
    updateTimeoutId,
    playCounted,
  ]);

  useEffect(() => {
    setPlayCounted(false);
  }, [currentTrackIndex, setPlayCounted]);

  useEffect(() => {
    if (isDesktop) {
      document.addEventListener("keydown", handleKeyPress);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress, isDesktop]);

  const changeIndex = (index: number) => {
    handlePause();
    dispatch(setIndex(index));
  };

  const handleShowPlaylistButton = () => {
    emitAnalyticsTrackingEvent("footer_player_show_playlist", {
      current_track_id: currentTrack?.id,
    });

    if (!expandFooter) {
      toggleFooterAndPlaylist(true);
    } else {
      setShowPlaylist(!showPlaylist);
    }
  };

  const closeExpandedFooter = () => {
    toggleFooterAndPlaylist(false);
  };

  useClickOutside(footerPlayerContainerRef, () => {
    toggleFooterAndPlaylist(false);
  });

  // useFetchFiles();
  // useDownloadTrack();
  useSetupOnFinish(isLooping);

  const limitView = !isDesktop && !expandFooter;

  useEffect(() => {
    if (!isFooterPlaying && timeoutId) {
      updateTimeoutId(null);
    }
  }, [timeoutId, isFooterPlaying, updateTimeoutId]);

  // useTrackPositionWhenLocked();

  const { queryKey } = useFetchTrackMp3({
    fileVersionId: currentTrack?.fileVersionId,
    enabled: Boolean(currentTrack?.id),
    code: code,
    isSnippet:
      currentTrack?.type === PlaylistTrackType.PortfolioProjectTrackResponse,
    projectId: currentTrack?.fileVersionId ? undefined : currentTrack?.id,
    spotifyPreviewUrl: currentTrack?.spotify_preview_url,
    onSuccess: () => {
      setActiveWaveformQueryKey({ queryKey });
    },
    extraAction: invalidateOnboardingProgress,
  });

  const renderPlaylistButton = () => {
    return (
      <PlaybackControlButton
        variant={PlaybackControlButtonVariant.PLAYLIST}
        size={PlaybackControlButtonSize.SMALL}
        onClick={handleShowPlaylistButton}
        isToggled={showPlaylist}
        disabled={Boolean(
          !playlist || playlist.length <= 0 || currentTrackIndex < 0,
        )}
      />
    );
  };

  return (
    <StyledFooterPlayerContainer
      ref={footerPlayerContainerRef}
      $expandFooter={expandFooter}
      $isFooterAndHeaderShowing={isFooterAndHeaderShowing}
      $showPlaylist={showPlaylist}
      $showFooterPlayer={showFooterPlayer && !hideFooter}
      onClick={() => {
        toggleFooterAndPlaylist(true);
      }}
    >
      {expandFooter && (
        <Button
          variant={ButtonVariant.UNSTYLED}
          sx={{ marginLeft: "auto" }}
          analyticsEvent={{
            name: "expand_footer_close",
            data: {
              current_track_id: currentTrack?.id,
            },
          }}
          onClick={(e: SyntheticEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            toggleFooterAndPlaylist(false);
          }}
        >
          <StyledFooterPlayerInfoFontawesomeIcon
            icon={faChevronDown}
            sx={{
              borderRadius: "50%",
              backgroundColor: (theme) => theme.palette.common.white,
              padding: (theme) => theme.spacing(0.5),
              opacity: 0.5,
            }}
            size="sm"
          />
        </Button>
      )}

      {showPlaylist && <Playlist onClick={closeExpandedFooter} />}
      {!showPlaylist && (
        <StyledFooterRowContainer
          $expandFooter={expandFooter}
          style={{ order: 1 }}
        >
          {!expandFooter && (
            <Button
              variant={ButtonVariant.UNSTYLED}
              onClick={(e: SyntheticEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                emitAnalyticsTrackingEvent("footer_player_close", {
                  current_track_id: currentTrack?.id,
                });
                handlePause();
                footerWaveformRef.current?.destroy();
                audioElement?.remove();
                dispatch(clearFileVersionDownload({ fileVersionId: null }));
                dispatch(resetAbPlayStore());
                setActiveWaveformQueryKey({ queryKey: [] });
              }}
            >
              <StyledFooterPlayerInfoFontawesomeIcon icon={faX} size="xs" />
            </Button>
          )}
          <FooterProjectInfo
            artworkSource={artworkSource}
            artistUser={artistName}
            engineerUser={engineerName}
            artistUsername={artistUsername}
            engineerUsername={engineerUsername}
            trackTitle={title}
            serviceType={serviceType}
            expandFooter={expandFooter}
            onClick={(e: SyntheticEvent<HTMLDivElement>) => {
              e.stopPropagation();
              emitAnalyticsTrackingEvent("footer_player_project_info", {
                current_track_id: currentTrack?.id,
              });
              setExpandFooter(!expandFooter);
            }}
            currentTrackIndex={currentTrackIndex + 1}
            showTrackIndex={canChangeIndex}
          />
        </StyledFooterRowContainer>
      )}

      <StyledFooterPlayerActionsContainer
        $expandFooter={expandFooter || !isDesktop}
        style={{ order: 4 }}
      >
        <StyledFooterPlayerPlaybackControlContainer
          $expandFooter={expandFooter}
          $isFooterAndHeaderShowing={isFooterAndHeaderShowing && !isDesktop}
        >
          {!limitView && (
            <>
              {/* Invisible icon fixes the alignment issue */}
              <PlaybackControlButton
                onClick={() => setIsLooping(!isLooping)}
                variant={PlaybackControlButtonVariant.REPEAT}
                size={
                  isMobile
                    ? PlaybackControlButtonSize.REGULAR
                    : PlaybackControlButtonSize.SMALL
                }
                isToggled={isLooping}
                hidden={true}
              />
              {playlist.length > 1 && (
                <PlaybackControlButton
                  onClick={() => {
                    emitAnalyticsTrackingEvent("footer_player_previous", {
                      current_track_id: currentTrack?.id,
                    });
                    if (canChangeIndex) {
                      changeIndex(currentTrackIndex - 1);
                    }
                  }}
                  variant={PlaybackControlButtonVariant.BACKWARD_STEP}
                  size={
                    isMobile
                      ? PlaybackControlButtonSize.REGULAR
                      : PlaybackControlButtonSize.SMALL
                  }
                />
              )}{" "}
              <PlaybackControlButton
                onClick={() => {
                  emitAnalyticsTrackingEvent("footer_player_back_step", {
                    current_track_id: currentTrack?.id,
                  });
                  skipSeconds(SkipTimes.BACKWARD_STEP, audioElement);
                }}
                variant={PlaybackControlButtonVariant.BACKWARD_FAST}
                size={
                  isMobile
                    ? PlaybackControlButtonSize.REGULAR
                    : PlaybackControlButtonSize.SMALL
                }
              />
            </>
          )}
          <PlaybackControlButton
            onClick={handlePlayPause}
            variant={
              isFooterPlaying
                ? PlaybackControlButtonVariant.PAUSE_CIRCLE
                : PlaybackControlButtonVariant.PLAY_CIRCLE
            }
            style={{ height: "28px", width: "28px", color: ColorPalette.White }}
          />
          {!limitView && (
            <>
              <PlaybackControlButton
                onClick={() => {
                  emitAnalyticsTrackingEvent("footer_player_forward_fast", {
                    current_track_id: currentTrack?.id,
                  });
                  skipSeconds(SkipTimes.FORWARD_STEP, audioElement);
                }}
                variant={PlaybackControlButtonVariant.FORWARD_FAST}
                size={
                  isMobile
                    ? PlaybackControlButtonSize.REGULAR
                    : PlaybackControlButtonSize.SMALL
                }
              />
              {playlist.length > 1 && (
                <PlaybackControlButton
                  onClick={() => {
                    emitAnalyticsTrackingEvent("footer_player_forward_step", {
                      current_track_id: currentTrack?.id,
                    });
                    if (canChangeIndex) {
                      changeIndex(currentTrackIndex + 1);
                    }
                  }}
                  variant={PlaybackControlButtonVariant.FORWARD_STEP}
                  size={
                    isMobile
                      ? PlaybackControlButtonSize.REGULAR
                      : PlaybackControlButtonSize.SMALL
                  }
                />
              )}
              <PlaybackControlButton
                onClick={() => {
                  emitAnalyticsTrackingEvent("footer_player_looping", {
                    current_track_id: currentTrack?.id,
                  });

                  setIsLooping(!isLooping);
                }}
                variant={PlaybackControlButtonVariant.REPEAT}
                size={
                  isMobile
                    ? PlaybackControlButtonSize.REGULAR
                    : PlaybackControlButtonSize.SMALL
                }
                isToggled={isLooping}
              />
            </>
          )}
        </StyledFooterPlayerPlaybackControlContainer>
      </StyledFooterPlayerActionsContainer>
      <StyledFooterWaveformContainer
        style={{ order: 3 }}
        $expandFooter={expandFooter}
      >
        <FooterWaveform />
      </StyledFooterWaveformContainer>
      {(isDesktop || expandFooter) && (
        <StyledFooterPlayerButtonsContainer
          style={{ order: expandFooter ? 2 : 4 }}
          $expandFooter={expandFooter}
        >
          {renderPlaylistButton()}
        </StyledFooterPlayerButtonsContainer>
      )}
    </StyledFooterPlayerContainer>
  );
};
